import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Activity, User, MapPin, Clock, Zap, ChevronDown, ChevronRight,
  Copy, Check, Loader, FolderPlus, Shield,
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../../utils/fetchWithTenant';
import CreateCaseModal from './CreateCaseModal';

const SEVERITY_COLORS = { INFO: '#60a5fa', WARNING: '#f59e0b', CRITICAL: '#ef4444' };

/** Short human summary of a CorrelationAction.actionTarget JSON blob. */
function actionTargetSummary(target) {
  if (!target || typeof target !== 'object') return '—';
  return (
    target.cameraName || target.cameraId || target.url ||
    target.webhookUrl || target.groupName || target.userId ||
    Object.values(target)[0] || '—'
  );
}

/** Small copy-to-clipboard control. */
function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button className="inv-copy" onClick={copy} title="Copy">
      <span>{value}</span>
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

/**
 * EventDetailPane — sliding right drawer with full event detail.
 *
 * Props:
 *   event         — the selected event row (or null to hide the pane)
 *   onClose       — () => void
 *   openCases     — array of open investigations for the "Add to Case" dropdown
 *   onCaseCreated — () => void   called after a case is created from this event
 *   onLinked      — () => void   called after the event is added to a case
 */
export default function EventDetailPane({ event, onClose, openCases = [], onCaseCreated, onLinked }) {
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [linking, setLinking]   = useState(false);

  // IQ subject dossier (only for OOSTO/FR events with a subject id).
  const [dossier, setDossier]             = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierError, setDossierError]   = useState(null);
  const dossierCacheRef = useRef(new Map()); // subjectId -> dossier data

  const loadDetail = useCallback(async (id) => {
    setLoading(true);
    setDetail(null);
    try {
      const r = await fetchWithTenant(`/api/investigations/events/${id}`);
      if (!r.ok) throw new Error('Failed to load event');
      setDetail(await r.json());
    } catch {
      toast.error('Failed to load event detail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (event?.id) {
      setShowMeta(false);
      loadDetail(event.id);
    }
  }, [event?.id, loadDetail]);

  // For an OOSTO/FR event the actor IS the FR subject; actorId is the IQ
  // subject lookup key. (EventDetailPane displays a CorrelationEvent, not an
  // EvidenceItem, so there is no .type/.frSubjectId here — see actorId.)
  const frSubjectId = (detail || event)?.sourceSystem === 'OOSTO'
    ? ((detail || event)?.actorId || null)
    : null;

  useEffect(() => {
    if (!frSubjectId) {
      setDossier(null);
      setDossierError(null);
      setDossierLoading(false);
      return undefined;
    }
    const cached = dossierCacheRef.current.get(frSubjectId);
    if (cached) {
      setDossier(cached);
      setDossierError(null);
      setDossierLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDossierLoading(true);
    setDossierError(null);
    setDossier(null);
    fetchWithTenant(`/api/iq/subjects/${frSubjectId}`, { method: 'GET' })
      .then(async (r) => {
        if (cancelled) return;
        if (r.ok) {
          const data = await r.json();
          dossierCacheRef.current.set(frSubjectId, data);
          setDossier(data);
          return;
        }
        let body = {};
        try { body = await r.json(); } catch { /* non-JSON error body */ }
        const errorCode = body && body.error;
        if (r.status === 404) {
          setDossierError({ kind: 'unavailable', errorCode, message: 'Dossier unavailable for this subject.' });
        } else if (errorCode === 'iq_not_configured' || errorCode === 'iq_connector_disabled') {
          // Persistent "not applicable" states — the render gate hides the
          // whole section; this message is only a fallback.
          setDossierError({ kind: 'unavailable', errorCode, message: 'IQ is not configured.' });
        } else if (errorCode === 'iq_agent_unavailable') {
          setDossierError({ kind: 'unavailable', errorCode, message: 'IQ is not currently available.' });
        } else if (errorCode === 'iq_misconfigured') {
          setDossierError({ kind: 'unavailable', errorCode, message: 'IQ is misconfigured — check the agent configuration.' });
        } else {
          setDossierError({ kind: 'error', errorCode, message: 'Failed to load dossier.' });
        }
      })
      .catch(() => {
        if (!cancelled) setDossierError({ kind: 'error', message: 'Failed to load dossier.' });
      })
      .finally(() => {
        if (!cancelled) setDossierLoading(false);
      });
    return () => { cancelled = true; };
  }, [frSubjectId]);

  if (!event) return null;

  // Prefer freshly-fetched detail; fall back to the list row while loading.
  const e = detail || event;
  const sevColor = SEVERITY_COLORS[e.severity] || '#94a3b8';
  const actions = detail?.actions || [];

  // Metadata: surface the feenics block (or full metadata sans raw payload).
  const metaToShow = e.metadata
    ? (e.metadata.feenics || (() => { const { raw, ...rest } = e.metadata; return rest; })())
    : null;

  // ── Oosto FR enrichment (opportunistic) ──
  // The CorrelationEvent metadata frequently carries the Oosto payload —
  // image, watchlists, confidence — directly. When the event came from
  // OOSTO, render a thumbnail / watchlist / confidence-coloured block in the
  // same visual rhythm as the InvestigationDetail FR_MATCH row.
  const isFr = e.sourceSystem === 'OOSTO';
  const frMeta = e.metadata || {};
  const frThumb = isFr ? (frMeta.imageUrl || frMeta.thumbnailUrl || frMeta.enrollmentPhotoUrl || null) : null;
  const frWatchlists = isFr && Array.isArray(frMeta.watchlistNames || frMeta.watchlists)
    ? (frMeta.watchlistNames || frMeta.watchlists)
    : [];
  const frConfRaw = isFr && typeof frMeta.confidence === 'number' ? frMeta.confidence : null;
  const frConf = frConfRaw == null
    ? null
    : Math.round(frConfRaw * (frConfRaw > 1 ? 1 : 100));
  const frConfClass = frConf == null
    ? ''
    : frConf >= 90 ? 'inv-confidence-high'
    : frConf >= 70 ? 'inv-confidence-mid'
    : 'inv-confidence-low';

  // Hide the dossier section entirely for persistent "not applicable" states
  // (IQ not configured for the org / connector off) — these are not
  // operational warnings worth surfacing. Transient/admin states
  // (iq_agent_unavailable, iq_misconfigured) and 404s still render a message.
  const isSilentlyUnavailable = dossierError?.errorCode === 'iq_not_configured'
    || dossierError?.errorCode === 'iq_connector_disabled';

  const handleAddToCase = async (caseId) => {
    if (!caseId) return;
    setLinking(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correlationEventId: event.id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to add to case');
      }
      toast.success('Event added to case');
      onLinked?.();
    } catch (err) {
      toast.error(err.message || 'Failed to add to case');
    } finally {
      setLinking(false);
    }
  };

  const handleCreateCase = async (payload) => {
    const r = await fetchWithTenant('/api/investigations/cases', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to create case');
    }
    toast.success('Case created');
    onCaseCreated?.();
  };

  return (
    <>
      <div className="inv-pane-backdrop" onClick={onClose} />
      <aside className="inv-pane" role="dialog" aria-label="Event detail">
        <div className="inv-pane-header">
          <div className="inv-pane-title">
            <Activity size={16} />
            <span>{e.eventTypeLabel || event.eventTypeLabel}</span>
          </div>
          <button className="inv-pane-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="inv-pane-body">
          {loading && !detail ? (
            <div className="inv-list-loading">
              <Loader size={20} className="spin" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              {/* ── ACTOR ── */}
              <div className="inv-pane-section">
                <p className="inv-pane-section-title"><User size={11} /> Actor</p>
                <div className="inv-detail-row" style={{ alignItems: 'center' }}>
                  {isFr && (
                    frThumb
                      ? (
                        <img
                          className="inv-fr-thumb"
                          src={frThumb}
                          alt={e.actorName || ''}
                          style={{ marginRight: '0.6rem' }}
                        />
                      )
                      : (
                        <div className="inv-fr-thumb-placeholder" style={{ marginRight: '0.6rem' }} aria-hidden="true">
                          {((e.actorName || '?').trim().charAt(0) || '?').toUpperCase()}
                        </div>
                      )
                  )}
                  <div style={{ flex: 1 }}>
                    <div className="inv-detail-row">
                      <span className="inv-detail-label">Name</span>
                      <span className="inv-detail-value">{e.actorName || 'Unknown'}</span>
                    </div>
                    <div className="inv-detail-row">
                      <span className="inv-detail-label">Type</span>
                      <span className="inv-detail-value">
                        <span className="inv-badge inv-badge-actor">{e.actorType || 'UNKNOWN'}</span>
                      </span>
                    </div>
                    {isFr && frConf != null && (
                      <div className="inv-detail-row">
                        <span className="inv-detail-label">Match</span>
                        <span className={`inv-detail-value ${frConfClass}`}>{frConf}%</span>
                      </div>
                    )}
                    {isFr && frWatchlists.length > 0 && (
                      <div className="inv-detail-row">
                        <span className="inv-detail-label">Watchlists</span>
                        <span className="inv-detail-value">
                          <span
                            className="inv-badge inv-badge-watchlist"
                            title={frWatchlists.join(', ')}
                          >
                            WATCHLIST
                          </span>
                          <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                            {frWatchlists.join(', ')}
                          </span>
                        </span>
                      </div>
                    )}
                    {e.actorId && (
                      <div className="inv-detail-row">
                        <span className="inv-detail-label">Actor ID</span>
                        <span className="inv-detail-value"><CopyValue value={e.actorId} /></span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── SOURCE DEVICE ── */}
              <div className="inv-pane-section">
                <p className="inv-pane-section-title"><MapPin size={11} /> Source Device</p>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">System</span>
                  <span className="inv-detail-value">
                    <span className="inv-badge inv-badge-system">{e.sourceSystem}</span>
                  </span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Device</span>
                  <span className="inv-detail-value">{e.sourceDeviceLabel || e.sourceDeviceId}</span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Device Type</span>
                  <span className="inv-detail-value">{e.sourceDeviceType}</span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Device ID</span>
                  <span className="inv-detail-value"><CopyValue value={e.sourceDeviceId} /></span>
                </div>
              </div>

              {/* ── EVENT ── */}
              <div className="inv-pane-section">
                <p className="inv-pane-section-title"><Clock size={11} /> Event</p>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Timestamp</span>
                  <span className="inv-detail-value">
                    {new Date(e.eventTimestamp).toLocaleString()}
                  </span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Severity</span>
                  <span className="inv-detail-value">
                    <span className="inv-badge" style={{ background: `${sevColor}26`, color: sevColor }}>
                      {e.severity}
                    </span>
                  </span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Correlation</span>
                  <span className="inv-detail-value">
                    <span className={`inv-badge inv-badge-status-${e.correlationStatus}`}>
                      {e.correlationStatus}
                    </span>
                  </span>
                </div>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">Event Type</span>
                  <span className="inv-detail-value" style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                    {e.eventType}
                  </span>
                </div>
              </div>

              {/* ── SUBJECT DOSSIER (IQ) — OOSTO/FR events only ── */}
              {isFr && e.actorId && !isSilentlyUnavailable && (
                <section className="inv-pane-section iq-dossier-section">
                  <p className="inv-pane-section-title iq-dossier-heading">
                    <Shield size={11} /> Subject Dossier
                  </p>

                  {dossierLoading && <div className="iq-dossier-loading">Loading dossier…</div>}

                  {dossierError && (
                    <div className={`iq-dossier-${dossierError.kind === 'unavailable' ? 'unavailable' : 'error'}`}>
                      {dossierError.message}
                    </div>
                  )}

                  {dossier && !dossierLoading && !dossierError && (
                    <>
                      <div className="iq-dossier-summary">
                        {dossier.name && <div><strong>{dossier.name}</strong></div>}
                        {dossier.external_id && <div>External ID: {dossier.external_id}</div>}
                      </div>

                      <div className="iq-dossier-recent">
                        <h4>Last 30 Days</h4>
                        <div>Events: {dossier.recent_30d?.event_count ?? 0}</div>
                        {dossier.recent_30d?.last_seen && (
                          <div>Last seen: {new Date(dossier.recent_30d.last_seen).toLocaleString()}</div>
                        )}
                      </div>

                      {dossier.watchlists && dossier.watchlists.length > 0 && (
                        <div className="iq-dossier-watchlists">
                          <h4>Watchlists</h4>
                          <ul>
                            {dossier.watchlists.map((wl) => (
                              <li key={wl.id}>{wl.name}{wl.is_local_only ? ' (local)' : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {dossier.detection_stats && (
                        <div className="iq-dossier-stats">
                          <h4>Detection History</h4>
                          <div>Total: {dossier.detection_stats.total}</div>
                          <div>Validated: {dossier.detection_stats.validated_count}</div>
                          {dossier.detection_stats.first_seen && (
                            <div>First seen: {new Date(dossier.detection_stats.first_seen).toLocaleDateString()}</div>
                          )}
                          {dossier.detection_stats.last_seen && (
                            <div>Last seen: {new Date(dossier.detection_stats.last_seen).toLocaleDateString()}</div>
                          )}
                        </div>
                      )}

                      {dossier.prior_incident_count > 0 && (
                        <div className="iq-dossier-incidents">
                          <h4>Prior Incidents ({dossier.prior_incident_count} total)</h4>
                          {dossier.incidents && dossier.incidents.length > 0 && (
                            <ul>
                              {dossier.incidents.map((inc) => (
                                <li key={inc.id}>
                                  <strong>{inc.title}</strong>
                                  {inc.finalized_at && (
                                    <span> — {new Date(inc.finalized_at).toLocaleDateString()}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {/* ── ACTIONS ── */}
              {actions.length > 0 && (
                <div className="inv-pane-section">
                  <p className="inv-pane-section-title"><Zap size={11} /> Actions ({actions.length})</p>
                  {actions.map(a => (
                    <div key={a.id} className="inv-action-item">
                      <span className="inv-action-type">{a.actionType}</span>
                      <span className="inv-action-target" title={String(actionTargetSummary(a.actionTarget))}>
                        {actionTargetSummary(a.actionTarget)}
                      </span>
                      <span className={`inv-badge inv-badge-status-${a.status}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── METADATA (collapsible) ── */}
              {metaToShow && (
                <div className="inv-pane-section">
                  <button className="inv-collapse-btn" onClick={() => setShowMeta(s => !s)}>
                    {showMeta ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Metadata
                  </button>
                  {showMeta && (
                    <pre className="inv-metadata-pre">{JSON.stringify(metaToShow, null, 2)}</pre>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="inv-pane-footer">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <FolderPlus size={14} /> Create Case
          </button>
          {openCases.length > 0 && (
            <select
              className="inv-select"
              defaultValue=""
              disabled={linking}
              onChange={e => handleAddToCase(e.target.value)}
            >
              <option value="" disabled>Add to Case…</option>
              {openCases.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}
        </div>
      </aside>

      <CreateCaseModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreateCase}
        event={event}
      />
    </>
  );
}
